import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getAdminSession, getAdminSessions, updateSessionsDetails } from '@/lib/api-calls';
import {
  distinct,
  formatDateWithDayOfWeek,
  formatTimeRange,
  FrequentTimeZones,
  isSessionStatusMoveValid,
  partitionBy,
} from '@airreading/common';
import { AdminSession, SessionStatus } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

interface SessionStatusChangeModalProps {
  sessions: AdminSession[];
  trigger: ReactNode;
  targetStatus: SessionStatus;
  modalTitle: string;
  confirmBtnLabel: string;
  description: string;
  noValidSessionsMessage: string;
  invalidSessionsMessage: string;
  buttonVariant?: 'default' | 'destructive';
  onSuccess?: () => void;
}

const formSchema = z.object({});

export const SessionStatusChangeModal = ({
  sessions,
  trigger,
  targetStatus,
  modalTitle,
  confirmBtnLabel,
  description,
  noValidSessionsMessage,
  invalidSessionsMessage,
  buttonVariant = 'default',
  onSuccess,
}: SessionStatusChangeModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: updateSessionsDetails,
  });

  const [validSessions, invalidSessions] = partitionBy(sessions, (session) =>
    isSessionStatusMoveValid(session.status, targetStatus),
  );

  const getActionTexts = (status: string) => {
    switch (status) {
      case SessionStatus.cancelled:
        return { actionText: 'cancel', pastTenseActionText: 'cancelled' };
      case SessionStatus.scheduled:
        return { actionText: 'restore', pastTenseActionText: 'restored' };
      case SessionStatus.completed:
        return { actionText: 'complete', pastTenseActionText: 'completed' };
      case SessionStatus.in_progress:
        return { actionText: 'start', pastTenseActionText: 'started' };
      default:
        return { actionText: 'update', pastTenseActionText: 'updated' };
    }
  };

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const actionText = getActionTexts(targetStatus).actionText;
    const pastTenseActionText = getActionTexts(targetStatus).pastTenseActionText;

    return mutateAsync({ sessionIds: validSessions.map((session) => session.id), status: targetStatus })
      .then(({ successSessionIds, errors, failedSessionIds }) => {
        if (successSessionIds.length > 0) {
          toast({
            title: `${pastTenseActionText.charAt(0).toUpperCase() + pastTenseActionText.slice(1)} ${successSessionIds.length} sessions`,
          });
        }
        if (failedSessionIds.length > 0) {
          toast({ title: `Failed to ${actionText} ${failedSessionIds.length} sessions` });
        }
        if (errors.length > 0) {
          toast({
            title: `Errors`,
            variant: 'destructive',
            duration: 15000,
            description: (
              <ul>
                {errors.map((e) => (
                  <li>{e}</li>
                ))}
              </ul>
            ),
          });
        }
        queryClient.invalidateQueries(getAdminSessions());
        Promise.all(successSessionIds.map((id) => queryClient.invalidateQueries(getAdminSession(id))));
        closeModal();
        onSuccess?.();
      })
      .catch(() => {
        toast({ title: `Failed to ${actionText} sessions`, variant: 'destructive' });
      });
  };

  return (
    <FormModal
      modalTitle={modalTitle}
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: validSessions.length === 0,
        variant: buttonVariant,
      }}
      confirmBtnLabel={confirmBtnLabel}
    >
      {() => {
        return (
          <div className="my-6">
            {validSessions.length === 0 ? (
              <div>{noValidSessionsMessage}</div>
            ) : (
              <>
                {invalidSessions.length > 0 && (
                  <div className="mb-4 text-amber-600">
                    {invalidSessionsMessage.replace('{count}', invalidSessions.length.toString())}
                  </div>
                )}

                {validSessions.length > 0 && (
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">{description}</p>
                    </div>

                    <div className="space-y-3">
                      {validSessions.map((session) => (
                        <div key={session.id} className="border rounded-lg p-3 bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium">
                                {formatDateWithDayOfWeek(session.startInstant, FrequentTimeZones.UsCentral)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {formatTimeRange(session.startInstant, session.endInstant, FrequentTimeZones.UsCentral)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {session.school?.name || 'No school assigned'}
                              </div>
                              <div className="text-sm text-gray-600">
                                Grades:{' '}
                                {Object.values(session.gradeByStudentId).filter(distinct()).join(', ') ||
                                  'Not specified'}
                              </div>
                              <div className="text-sm text-gray-600">
                                Students: {session.students.map((s) => s.fullNameOrEmail).join(', ')}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {session.coach?.fullNameOrEmail || 'No teacher assigned'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};
